/**
 * Layer Group Manager Service
 *
 * Manages the organization of trip features into layer groups
 * for FEAT-010 Interactive Trip Feature Drawing and Management.
 */

import { db } from '../database/schema.js'
import type { LayerGroup, LayerType, Trip } from '../database/schema.js'
import type { Feature } from '../schemas/feature.js'
import { edgeService, nodeRef, parseNodeRef } from './edge-service.js'
import {
  validateLayerGroup,
  sanitizeUserInput,
  validateUserInputSecurity,
} from '../storage/validation.js'
import { featureStorage } from '../storage/feature-storage.js'
import { authService } from './auth/auth-service.js'
import { isApplyingServerChanges } from '../storage/server-apply.js'
import { schedulePush } from '../storage/sync-push-scheduler.js'
import type { SyncEvent } from '../database/schema.js'

/** Derive a deterministic UUID from a user sub + namespace so all devices converge on the same ID */
async function deterministicId(userSub: string, namespace: string): Promise<string> {
  const data = new TextEncoder().encode(`${namespace}:${userSub}`)
  const hash = await crypto.subtle.digest('SHA-256', data)
  const bytes = new Uint8Array(hash)
  const hex = Array.from(bytes.slice(0, 16), b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`
}

export interface CreateLayerGroupData {
  name: string
  description?: string
  color: string
  icon?: string
  layerType?: LayerType
  isSystemGroup?: boolean
  parentLayerId?: string
  tripId?: string
  displayOrder?: number
}

export interface LayerTreeNode {
  layer: LayerGroup
  children: LayerTreeNode[]
  features: Feature[]
  section?: 'features' | 'plans' | 'activities' | 'trips'
}

export class LayerGroupManager {
  private static instance: LayerGroupManager | undefined
  private layerPanelOpen = false
  private lastUsedLayerId: string | null = null

  static getInstance(): LayerGroupManager {
    LayerGroupManager.instance ??= new LayerGroupManager()
    return LayerGroupManager.instance
  }

  // ===== Layer Panel Management =====
  openLayerPanel(): void {
    this.layerPanelOpen = true
  }

  closeLayerPanel(): void {
    this.layerPanelOpen = false
  }

  isLayerPanelOpen(): boolean {
    return this.layerPanelOpen
  }

  // ===== Layer Group Creation =====
  async createLayerGroup(name: string, properties?: Partial<LayerGroup>): Promise<LayerGroup> {
    // Sanitize user inputs
    const sanitizedName = sanitizeUserInput(name)
    const sanitizedDescription = properties?.description
      ? sanitizeUserInput(properties.description)
      : undefined
    const sanitizedIcon = properties?.icon ? sanitizeUserInput(properties.icon) : undefined

    // Validate sanitized inputs
    const nameSecurityCheck = validateUserInputSecurity(sanitizedName)
    if (!nameSecurityCheck.isValid) {
      throw new Error(
        `Security validation failed for layer name: ${nameSecurityCheck.errors[0]?.message}`
      )
    }

    if (sanitizedDescription) {
      const descSecurityCheck = validateUserInputSecurity(sanitizedDescription)
      if (!descSecurityCheck.isValid) {
        throw new Error(
          `Security validation failed for layer description: ${descSecurityCheck.errors[0]?.message}`
        )
      }
    }

    if (sanitizedIcon) {
      const iconSecurityCheck = validateUserInputSecurity(sanitizedIcon)
      if (!iconSecurityCheck.isValid) {
        throw new Error(
          `Security validation failed for layer icon: ${iconSecurityCheck.errors[0]?.message}`
        )
      }
    }

    const existingGroups = await this.getAllLayerGroups()
    const maxOrder =
      existingGroups.length > 0 ? Math.max(...existingGroups.map(g => g.displayOrder)) : 0

    const layerGroup: LayerGroup = {
      id: crypto.randomUUID(),
      name: sanitizedName,
      description: sanitizedDescription,
      color: properties?.color ?? this.getDefaultColor(),
      icon: sanitizedIcon,
      layerType: properties?.layerType ?? 'custom',
      isSystemGroup: properties?.isSystemGroup ?? false,
      parentLayerId: properties?.parentLayerId,
      tripId: properties?.tripId,
      displayOrder: properties?.displayOrder ?? maxOrder + 1,
      isVisible: properties?.isVisible !== false,
      isExpanded: properties?.isExpanded !== false,
      featureIds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: authService.getCurrentUserId(),
    }

    // Validate the complete layer group before saving
    const validation = validateLayerGroup(layerGroup, { context: 'create' })
    if (!validation.isValid) {
      throw new Error(
        `Layer group validation failed: ${validation.errors.map(e => e.message).join(', ')}`
      )
    }

    await db.layerGroups.add(layerGroup)
    if (!isApplyingServerChanges()) {
      const event: SyncEvent = {
        id: crypto.randomUUID(),
        entityType: 'layerGroup',
        entityId: layerGroup.id,
        operation: 'create',
        data: { ...(layerGroup as unknown as Record<string, unknown>) },
        timestamp: new Date().toISOString(),
        conflictResolved: false,
      }
      db.syncEvents.add(event).catch(() => {})
      schedulePush()
    }
    if (!layerGroup.isSystemGroup) {
      this.lastUsedLayerId = layerGroup.id
    }
    return layerGroup
  }

  // ===== Track Cleanup =====

  private static haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const toRad = (d: number): number => (d * Math.PI) / 180
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
    return 2 * Math.asin(Math.sqrt(a)) * 6371000
  }

  /**
   * Clean all tracks by:
   * 1. Removing coarse location fixes (accuracy >= 100m)
   * 2. Deduplicating consecutive identical coordinates
   * 3. Removing spike points (stale GPS fixes that jump far away then back)
   */
  async cleanAllTracks(): Promise<void> {
    const allTracks = await db.tracks.toArray()
    for (const track of allTracks) {
      const geojson = track.geojson
      if (!('features' in geojson)) {
        continue
      }
      const feature = geojson.features[0]

      const lineGeometry = feature.geometry as GeoJSON.LineString
      const coords: number[][] = lineGeometry.coordinates
      const accuracies: number[] = (feature.properties?.accuracies as number[] | undefined) ?? []
      const timestamps: number[] = (feature.properties?.timestamps as number[] | undefined) ?? []

      // Pass 1: Remove coarse fixes and consecutive duplicates
      const pass1Coords: number[][] = []
      const pass1Acc: number[] = []
      const pass1Time: number[] = []
      const pass1OrigIdx: number[] = [] // track original indices for spike detection

      for (let i = 0; i < coords.length; i++) {
        if (accuracies[i] >= 100) {
          continue
        }
        if (pass1Coords.length > 0) {
          const prev = pass1Coords[pass1Coords.length - 1]
          if (prev[0] === coords[i][0] && prev[1] === coords[i][1]) {
            continue
          }
        }
        pass1Coords.push(coords[i])
        pass1Acc.push(accuracies[i])
        pass1Time.push(timestamps[i])
        pass1OrigIdx.push(i)
      }

      // Pass 2: Remove spikes — points that jump far from BOTH neighbors
      // A spike is a point where dist(prev, point) > 200m AND dist(point, next) > 200m
      // but dist(prev, next) < 200m (neighbors are close, point is the outlier)
      const spikeIndices = new Set<number>()
      for (let i = 1; i < pass1Coords.length - 1; i++) {
        const prev = pass1Coords[i - 1]
        const curr = pass1Coords[i]
        const next = pass1Coords[i + 1]
        // coords are [lng, lat]
        const dPrev = LayerGroupManager.haversine(prev[1], prev[0], curr[1], curr[0])
        const dNext = LayerGroupManager.haversine(curr[1], curr[0], next[1], next[0])
        const dSpan = LayerGroupManager.haversine(prev[1], prev[0], next[1], next[0])
        if (dPrev > 200 && dNext > 200 && dSpan < 200) {
          spikeIndices.add(i)
        }
      }

      const cleanCoords: number[][] = []
      const cleanAcc: number[] = []
      const cleanTime: number[] = []
      for (let i = 0; i < pass1Coords.length; i++) {
        if (spikeIndices.has(i)) {
          continue
        }
        cleanCoords.push(pass1Coords[i])
        cleanAcc.push(pass1Acc[i])
        cleanTime.push(pass1Time[i])
      }

      if (cleanCoords.length === coords.length) {
        continue
      } // Nothing changed

      const cleanGeojson = {
        ...geojson,
        features: [
          {
            ...feature,
            properties: {
              ...feature.properties,
              accuracies: cleanAcc,
              timestamps: cleanTime,
            },
            geometry: {
              ...feature.geometry,
              coordinates: cleanCoords,
            },
          },
        ],
      }

      await db.tracks.update(track.id, {
        geojson: cleanGeojson,
        pointCount: cleanCoords.length,
      })

      // Also update any corresponding Feature
      const tripFeatures = await db.features.toArray()
      for (const tf of tripFeatures) {
        if (tf.name === track.name && tf.type === 'line' && tf.geometry.type === 'LineString') {
          await db.features.update(tf.id, {
            geometry: { type: 'LineString', coordinates: cleanCoords },
          })
        }
      }
    }
  }

  // ===== Trip Track Materialization =====

  /**
   * Ensure every trip that has Track records also has corresponding
   * Feature (line) records so they appear in the layer tree and render on the map.
   */
  async materializeTracksForAllTrips(): Promise<void> {
    const allTrips = await db.trips.toArray()
    if (allTrips.length === 0) {
      return
    }

    const allTracks = await db.tracks.toArray()
    if (allTracks.length === 0) {
      return
    }

    // Group tracks by tripId
    const tracksByTrip = new Map<string, typeof allTracks>()
    for (const track of allTracks) {
      if (!track.tripId) {
        continue
      }
      const list = tracksByTrip.get(track.tripId) ?? []
      list.push(track)
      tracksByTrip.set(track.tripId, list)
    }

    for (const trip of allTrips) {
      const tracks = tracksByTrip.get(trip.id)
      if (!tracks || tracks.length === 0) {
        continue
      }

      const tripLayers = await this.ensureTripLayers(trip.id, trip.name)

      // Check which tracks already have a Feature in the tracks layer
      const existingFeatures = await db.features
        .where('layerId')
        .equals(tripLayers.tracks.id)
        .toArray()
      const existingTrackNames = new Set(existingFeatures.map(f => f.name))

      for (const track of tracks) {
        // Skip if a feature with this track's name already exists in the layer
        if (existingTrackNames.has(track.name)) {
          continue
        }

        const featureIds = await this.importGeoJSONToLayer(
          track.geojson as GeoJSON.FeatureCollection,
          tripLayers.tracks.id
        )
        for (const fid of featureIds) {
          await db.features.update(fid, {
            tripIds: [trip.id],
            name: track.name,
          })
        }
      }
    }
  }

  // ===== Trip-Based Layer Groups =====
  async ensureTripLayers(
    tripId: string,
    tripName: string
  ): Promise<{ trip: LayerGroup; tracks: LayerGroup; features: LayerGroup }> {
    const existing = await db.layerGroups
      .toArray()
      .then(all => all.filter(l => l.tripId === tripId))
    let tripLayer = existing.find(l => l.layerType === 'trip')
    let tracksLayer = existing.find(l => l.layerType === 'tracks')
    let featuresLayer = existing.find(l => l.layerType === 'features')

    tripLayer ??= await this.createLayerGroup(tripName, {
      color: '#6366f1',
      icon: 'hiking',
      layerType: 'trip',
      isSystemGroup: true,
      tripId,
    })

    tracksLayer ??= await this.createLayerGroup('Tracks', {
      color: '#3b82f6',
      icon: 'route',
      layerType: 'tracks',
      isSystemGroup: true,
      parentLayerId: tripLayer.id,
      tripId,
    })

    featuresLayer ??= await this.createLayerGroup('Features', {
      color: '#10b981',
      icon: 'layers',
      layerType: 'features',
      isSystemGroup: true,
      parentLayerId: tripLayer.id,
      tripId,
    })

    return { trip: tripLayer, tracks: tracksLayer, features: featuresLayer }
  }

  /**
   * Delete all data associated with a trip: layer groups, features, tracks, waypoints, and the trip itself.
   */
  async deleteTrip(tripId: string): Promise<void> {
    // Delete layer groups and their features
    const layerGroups = await db.layerGroups
      .toArray()
      .then(all => all.filter(l => l.tripId === tripId))
    for (const group of layerGroups) {
      // Delete features in each layer group
      const features = await db.features.where('layerId').equals(group.id).toArray()
      for (const feature of features) {
        await featureStorage.deleteFeature(feature.id)
      }
      await db.layerGroups.delete(group.id)
    }

    // Remove trip association from any features that reference this trip in tripIds
    const allFeatures = await db.features.toArray()
    for (const feature of allFeatures) {
      if (feature.tripIds?.includes(tripId)) {
        const updatedIds = feature.tripIds.filter((id: string) => id !== tripId)
        await db.features.update(feature.id, { tripIds: updatedIds })
      }
    }

    // Delete tracks and waypoints
    await db.tracks.where('tripId').equals(tripId).delete()
    await db.waypoints.where('tripId').equals(tripId).delete()

    // Delete the trip itself
    await db.trips.delete(tripId)
  }

  setLastUsedLayer(layerId: string): void {
    this.lastUsedLayerId = layerId
  }

  async getDefaultFeatureLayer(tripId?: string): Promise<LayerGroup> {
    if (this.lastUsedLayerId) {
      const lastUsed = await this.getLayerGroup(this.lastUsedLayerId)
      if (lastUsed) {
        return lastUsed
      }
    }

    if (tripId) {
      const tripLayers = await db.layerGroups
        .toArray()
        .then(all => all.filter(l => l.tripId === tripId))
      const featuresLayer = tripLayers.find(l => l.layerType === 'features')
      if (featuresLayer) {
        return featuresLayer
      }
    }

    return this.ensureFeaturesRoot()
  }

  /**
   * Ensure the global "Features" root layer exists. Migrates old "Uncategorized" if present.
   */
  async ensureFeaturesRoot(): Promise<LayerGroup> {
    const userId = authService.getCurrentUserId()

    // Compute the canonical deterministic ID for this user's features root
    const canonicalId = userId ? await deterministicId(userId, 'features-root') : null

    const allLayers = await this.getAllLayerGroups()

    // Look for the canonical group first
    let featuresRoot = canonicalId ? allLayers.find(l => l.id === canonicalId) : null

    // Fall back to finding by name/flags (legacy groups)
    if (!featuresRoot) {
      featuresRoot = allLayers.find(
        l => l.name === 'Features' && l.isSystemGroup && !l.tripId && !l.parentLayerId
      )
    }

    if (!featuresRoot) {
      const uncategorized = allLayers.find(
        l => l.name === 'Uncategorized' && l.isSystemGroup && !l.tripId
      )
      if (uncategorized) {
        await db.layerGroups.update(uncategorized.id, { name: 'Features', icon: 'layers' })
        featuresRoot = { ...uncategorized, name: 'Features', icon: 'layers' }
      }
    }

    // Migrate legacy group to canonical ID
    if (featuresRoot && canonicalId && featuresRoot.id !== canonicalId) {
      const oldId = featuresRoot.id
      const migrated: LayerGroup = { ...featuresRoot, id: canonicalId }
      await db.layerGroups.add(migrated)
      // Move feature edges from old group to new group
      const oldGroupRef = nodeRef('group', oldId)
      const newGroupRef = nodeRef('group', canonicalId)
      const featureEdges = await edgeService.getBySubject(oldGroupRef, 'has-feature')
      for (const edge of featureEdges) {
        await edgeService.create({
          subject: newGroupRef,
          predicate: 'has-feature',
          object: edge.object,
        })
      }
      // Update features' layerId reference
      const featureIds = featureEdges.map(e => parseNodeRef(e.object).id)
      for (const fid of featureIds) {
        await db.features.update(fid, { layerId: canonicalId })
      }
      // Remove old group and its edges
      await edgeService.deleteAllForNode(oldGroupRef)
      await db.layerGroups.delete(oldId)
      featuresRoot = migrated
    }

    // Create canonical group if none exists
    if (!featuresRoot && canonicalId) {
      const now = new Date().toISOString()
      featuresRoot = {
        id: canonicalId,
        name: 'Features',
        color: '#94a3b8',
        icon: 'layers',
        layerType: 'features',
        isSystemGroup: true,
        displayOrder: 1,
        isVisible: true,
        isExpanded: true,
        featureIds: [],
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        version: 0,
      } as LayerGroup
      await db.layerGroups.add(featuresRoot)
    } else if (!featuresRoot) {
      // No user ID available, fall back to random (shouldn't happen in normal flow)
      featuresRoot = await this.createLayerGroup('Features', {
        color: '#94a3b8',
        icon: 'layers',
        layerType: 'features',
        isSystemGroup: true,
      })
    }

    // Ensure layer group entity has been pushed to server
    if (
      (featuresRoot as unknown as { version?: number }).version === 0 &&
      !isApplyingServerChanges()
    ) {
      const hasPendingSync = await db.syncEvents.where('entityId').equals(featuresRoot.id).count()
      if (hasPendingSync === 0) {
        const event: SyncEvent = {
          id: crypto.randomUUID(),
          entityType: 'layerGroup',
          entityId: featuresRoot.id,
          operation: 'create',
          data: { ...(featuresRoot as unknown as Record<string, unknown>) },
          timestamp: new Date().toISOString(),
          conflictResolved: false,
        }
        db.syncEvents.add(event).catch(() => {})
        schedulePush()
      }
    }

    // Ensure user → has-layer-group → group edge exists
    if (userId) {
      const userRef = nodeRef('user', userId)
      const groupRef = nodeRef('group', featuresRoot.id)
      const hasEdge = await edgeService.exists(userRef, 'has-layer-group', groupRef)
      if (!hasEdge) {
        await edgeService.create({
          subject: userRef,
          predicate: 'has-layer-group',
          object: groupRef,
        })
      }
    }

    return featuresRoot
  }

  async getChildLayers(parentId: string): Promise<LayerGroup[]> {
    const all = await this.getAllLayerGroups()
    return all
      .filter(l => l.parentLayerId === parentId)
      .sort((a, b) => a.displayOrder - b.displayOrder)
  }

  async getLayerTree(): Promise<LayerTreeNode[]> {
    // Clean up orphaned trip layers on every load
    await this.cleanupOrphanedTripLayers()

    // Ensure all trips with tracks have layer groups and Features
    await this.materializeTracksForAllTrips()

    const allFeatures = await db.features.toArray()
    const featureMap = new Map(allFeatures.map(f => [f.id, f]))

    const featuresByLayer = new Map<string, Feature[]>()
    for (const f of allFeatures) {
      if (f.layerId) {
        const list = featuresByLayer.get(f.layerId) ?? []
        list.push(f)
        featuresByLayer.set(f.layerId, list)
      }
    }

    // Also discover features via has-feature edges (for synced features without layerId)
    const allEdges = await db.edges.toArray()
    for (const edge of allEdges) {
      if (edge.predicate !== 'has-feature') continue
      const { type: subjType, id: groupId } = parseNodeRef(edge.subject)
      if (subjType !== 'group') continue
      const { id: featureId } = parseNodeRef(edge.object)
      const feature = featureMap.get(featureId)
      if (!feature) continue
      const existing = featuresByLayer.get(groupId)
      if (existing?.some(f => f.id === featureId)) continue
      const list = existing ?? []
      list.push(feature)
      featuresByLayer.set(groupId, list)
    }

    // Ensure Features root exists (migrates Uncategorized if needed)
    await this.ensureFeaturesRoot()
    // Re-fetch in case ensureFeaturesRoot created/updated a layer
    const freshLayers = await this.getAllLayerGroups()

    // Rebuild buildNode with fresh data
    const buildFreshNode = (layer: LayerGroup, excludeIds?: Set<string>): LayerTreeNode => {
      const children = freshLayers
        .filter(l => l.parentLayerId === layer.id && !excludeIds?.has(l.id))
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(l => buildFreshNode(l, excludeIds))
      const rawFeatures = featuresByLayer.get(layer.id) ?? []
      const orderedFeatures: Feature[] = []
      if (layer.featureIds.length > 0) {
        const featureMap = new Map(rawFeatures.map(f => [f.id, f]))
        for (const fid of layer.featureIds) {
          const f = featureMap.get(fid)
          if (f) {
            orderedFeatures.push(f)
            featureMap.delete(fid)
          }
        }
        for (const f of featureMap.values()) {
          orderedFeatures.push(f)
        }
      } else {
        orderedFeatures.push(...rawFeatures)
      }
      return { layer, children, features: orderedFeatures }
    }

    const tripRoots = freshLayers
      .filter(l => l.layerType === 'trip' && !l.parentLayerId)
      .sort((a, b) => a.displayOrder - b.displayOrder)

    const featuresRoot = freshLayers.find(
      l => l.name === 'Features' && l.isSystemGroup && !l.tripId && !l.parentLayerId
    )

    // Resolve plan/activity root groups via edges
    const userId = authService.getCurrentUserId()
    const [plans, activities] = await Promise.all([
      db.plans
        .toArray()
        .then(all => all.filter(p => (p as Record<string, unknown>).accessStatus !== 'revoked')),
      db.activities
        .toArray()
        .then(all => all.filter(a => (a as Record<string, unknown>).accessStatus !== 'revoked')),
    ])

    const entityRootGroupIds = new Set<string>()

    const planNodes: LayerTreeNode[] = []
    for (const plan of plans) {
      const groupIds = await edgeService.getObjectIds(nodeRef('plan', plan.id), 'has-layer-group')
      const rootGroup =
        groupIds.length > 0 ? freshLayers.find(l => l.id === groupIds[0]) : undefined
      if (rootGroup) {
        entityRootGroupIds.add(rootGroup.id)
        planNodes.push(buildFreshNode(rootGroup))
      } else {
        // Use group ID from edge if available so featuresByLayer can match
        const virtualId = groupIds.length > 0 ? groupIds[0] : plan.id
        entityRootGroupIds.add(virtualId)
        const features = featuresByLayer.get(virtualId) ?? []
        planNodes.push({
          layer: {
            id: virtualId,
            name: plan.name,
            color: '#8b5cf6',
            layerType: 'custom',
            isSystemGroup: false,
            displayOrder: 0,
            isVisible: true,
            isExpanded: true,
            featureIds: [],
            createdBy: userId,
            createdAt: plan.createdAt,
            updatedAt: plan.updatedAt,
          } as LayerGroup,
          children: [],
          features,
        })
      }
    }

    const activityNodes: LayerTreeNode[] = []
    for (const activity of activities) {
      const groupIds = await edgeService.getObjectIds(
        nodeRef('activity', activity.id),
        'has-layer-group'
      )
      const rootGroup =
        groupIds.length > 0 ? freshLayers.find(l => l.id === groupIds[0]) : undefined
      if (rootGroup) {
        entityRootGroupIds.add(rootGroup.id)
        activityNodes.push(buildFreshNode(rootGroup))
      } else {
        const virtualId = groupIds.length > 0 ? groupIds[0] : activity.id
        entityRootGroupIds.add(virtualId)
        const features = featuresByLayer.get(virtualId) ?? []
        activityNodes.push({
          layer: {
            id: virtualId,
            name: activity.name ?? 'Activity',
            color: '#10b981',
            layerType: 'custom',
            isSystemGroup: false,
            displayOrder: 0,
            isVisible: true,
            isExpanded: true,
            featureIds: [],
            createdBy: userId,
            createdAt: activity.createdAt,
            updatedAt: activity.updatedAt,
          } as LayerGroup,
          children: [],
          features,
        })
      }
    }

    // Adopt orphaned root groups (exclude entity root groups)
    const orphanedRoots = freshLayers.filter(
      l =>
        !l.parentLayerId &&
        !l.tripId &&
        l !== featuresRoot &&
        l.layerType !== 'trip' &&
        !entityRootGroupIds.has(l.id)
    )
    for (const orphan of orphanedRoots) {
      if (featuresRoot) {
        await db.layerGroups.update(orphan.id, { parentLayerId: featuresRoot.id })
        orphan.parentLayerId = featuresRoot.id
      }
    }

    const roots: LayerTreeNode[] = []

    // 1. Features (personal) — exclude only entity root groups to prevent cross-section duplication
    if (featuresRoot) {
      roots.push({ ...buildFreshNode(featuresRoot, entityRootGroupIds), section: 'features' })
    }

    // 2. Plans
    for (const node of planNodes) {
      roots.push({ ...node, section: 'plans' })
    }

    // 3. Activities
    for (const node of activityNodes) {
      roots.push({ ...node, section: 'activities' })
    }

    // 4. Trips (legacy)
    for (const node of tripRoots.map(n => buildFreshNode(n))) {
      roots.push({ ...node, section: 'trips' })
    }

    return roots
  }

  /**
   * Clean up layer groups whose tripId references a trip that no longer exists.
   */
  async cleanupOrphanedTripLayers(): Promise<void> {
    const allLayers = await db.layerGroups.toArray()
    const tripLayers = allLayers.filter(l => l.tripId && l.tripId !== '')
    if (tripLayers.length === 0) {
      return
    }

    const tripIds = new Set(tripLayers.map(l => l.tripId!))
    const existingTrips = await db.trips.bulkGet([...tripIds])
    const existingTripIds = new Set(existingTrips.filter(Boolean).map(t => t!.id))

    for (const layer of tripLayers) {
      if (layer.tripId && !existingTripIds.has(layer.tripId)) {
        // Delete features in this orphaned layer
        const features = await db.features.where('layerId').equals(layer.id).toArray()
        for (const f of features) {
          await featureStorage.deleteFeature(f.id)
        }
        await db.layerGroups.delete(layer.id)
      }
    }
  }

  async createTripLayerGroup(trip: Trip): Promise<LayerGroup> {
    const { trip: tripLayer } = await this.ensureTripLayers(trip.id, trip.name)
    return tripLayer
  }

  // ===== Layer Group Management =====
  async getLayerGroup(layerId: string): Promise<LayerGroup | undefined> {
    return await db.layerGroups.get(layerId)
  }

  async getLayerGroupByName(name: string): Promise<LayerGroup | undefined> {
    const all = await db.layerGroups.toArray()
    return all.find(l => l.name === name)
  }

  async getAllLayerGroups(): Promise<LayerGroup[]> {
    return await db.layerGroups.orderBy('displayOrder').toArray()
  }

  async deleteLayerGroup(layerId: string): Promise<void> {
    const defaultLayer = await this.getDefaultFeatureLayer()

    const features = await db.features.where('layerId').equals(layerId).toArray()
    for (const feature of features) {
      await this.moveFeatureToLayer(feature.id, defaultLayer.id)
    }

    const children = await this.getChildLayers(layerId)
    for (const child of children) {
      await this.deleteLayerGroup(child.id)
    }

    await db.layerGroups.delete(layerId)
  }

  async updateLayerGroup(layerId: string, updates: Partial<LayerGroup>): Promise<void> {
    await db.layerGroups.update(layerId, {
      ...updates,
      updatedAt: new Date().toISOString(),
    })
  }

  // ===== Feature-Layer Association =====
  async moveFeatureToLayer(featureId: string, layerId: string): Promise<void> {
    const feature = await db.features.get(featureId)
    if (feature?.layerId) {
      await this.removeFeatureFromLayer(featureId, feature.layerId)
    }
    await this.addFeatureToLayer(featureId, layerId)
  }

  async moveFeatureToLayerAtIndex(
    featureId: string,
    layerId: string,
    index: number
  ): Promise<void> {
    const feature = await db.features.get(featureId)
    const oldLayerId = feature?.layerId

    if (oldLayerId && oldLayerId !== layerId) {
      await this.removeFeatureFromLayer(featureId, oldLayerId)
    }

    await db.features.update(featureId, { layerId })

    const layer = await this.getLayerGroup(layerId)
    if (layer) {
      const actualFeatures = await db.features.where('layerId').equals(layerId).toArray()
      const actualIds = new Set(actualFeatures.map(f => f.id))
      actualIds.add(featureId)
      const featureIds = layer.featureIds.filter(id => actualIds.has(id) && id !== featureId)
      for (const f of actualFeatures) {
        if (f.id !== featureId && !featureIds.includes(f.id)) {
          featureIds.push(f.id)
        }
      }
      const clampedIndex = Math.min(index, featureIds.length)
      featureIds.splice(clampedIndex, 0, featureId)
      await this.updateLayerGroup(layerId, { featureIds })
    }
  }

  async addFeatureToLayer(featureId: string, layerId: string): Promise<void> {
    // Update feature
    await db.features.update(featureId, { layerId })

    // Update layer group's feature list
    const layer = await this.getLayerGroup(layerId)
    if (layer) {
      const featureIds = [...layer.featureIds]
      if (!featureIds.includes(featureId)) {
        featureIds.push(featureId)
        await this.updateLayerGroup(layerId, { featureIds })
      }
    }
  }

  async removeFeatureFromLayer(featureId: string, layerId: string): Promise<void> {
    // Update feature
    await db.features.update(featureId, { layerId: undefined })

    // Update layer group's feature list
    const layer = await this.getLayerGroup(layerId)
    if (layer) {
      const featureIds = layer.featureIds.filter(id => id !== featureId)
      await this.updateLayerGroup(layerId, { featureIds })
    }
  }

  async getFeaturesInLayer(layerId: string): Promise<Feature[]> {
    let features = await db.features.where('layerId').equals(layerId).toArray()
    if (features.length === 0) {
      const all = await db.features.toArray()
      features = all.filter(f => f.layerId === layerId)
    }
    return features
  }

  // ===== Layer Visibility =====
  async setLayerVisibility(layerId: string, visible: boolean): Promise<void> {
    await this.updateLayerGroup(layerId, { isVisible: visible })

    const features = await this.getFeaturesInLayer(layerId)
    for (const feature of features) {
      await db.features.update(feature.id, { isVisible: visible })
    }

    const allLayers = await this.getAllLayerGroups()
    const children = allLayers.filter(l => l.parentLayerId === layerId)
    for (const child of children) {
      await this.setLayerVisibility(child.id, visible)
    }
  }

  async getLayerVisibilityState(layerId: string): Promise<'all' | 'some' | 'none'> {
    const layer = await this.getLayerGroup(layerId)
    if (!layer) {
      return 'none'
    }

    const features = await this.getFeaturesInLayer(layerId)
    const children = await this.getChildLayers(layerId)

    const visibleItems: boolean[] = []
    for (const f of features) {
      visibleItems.push(f.isVisible)
    }
    for (const child of children) {
      const childState = await this.getLayerVisibilityState(child.id)
      if (childState === 'all') {
        visibleItems.push(true)
      } else if (childState === 'none') {
        visibleItems.push(false)
      } else {
        return 'some'
      }
    }

    if (visibleItems.length === 0) {
      return layer.isVisible ? 'all' : 'none'
    }
    const allVisible = visibleItems.every(v => v)
    const noneVisible = visibleItems.every(v => !v)
    if (allVisible) {
      return 'all'
    }
    if (noneVisible) {
      return 'none'
    }
    return 'some'
  }

  async toggleLayerExpanded(layerId: string): Promise<void> {
    const layer = await this.getLayerGroup(layerId)
    if (layer) {
      await this.updateLayerGroup(layerId, { isExpanded: !layer.isExpanded })
    }
  }

  // ===== Layer Organization =====
  async reorderLayers(layerIds: string[]): Promise<void> {
    for (let i = 0; i < layerIds.length; i++) {
      await this.updateLayerGroup(layerIds[i], { displayOrder: i })
    }
  }

  // ===== Validation Methods =====
  async layerGroupExists(name: string): Promise<boolean> {
    const layer = await this.getLayerGroupByName(name)
    return !!layer
  }

  async isSystemGroup(layerId: string): Promise<boolean> {
    const layer = await this.getLayerGroup(layerId)
    return layer?.isSystemGroup ?? false
  }

  async isFeatureInLayer(featureId: string, layerId: string): Promise<boolean> {
    const feature = await db.features.get(featureId)
    return feature?.layerId === layerId
  }

  async isFeatureInLayerByName(featureId: string, layerName: string): Promise<boolean> {
    const layer = await this.getLayerGroupByName(layerName)
    if (!layer) {
      return false
    }
    return this.isFeatureInLayer(featureId, layer.id)
  }

  // ===== Import/Export =====
  async exportLayerAsGeoJSON(
    layerId: string
  ): Promise<GeoJSON.FeatureCollection & { properties?: Record<string, unknown> }> {
    const features = await this.getFeaturesInLayer(layerId)
    const layer = await this.getLayerGroup(layerId)

    const geoJsonFeatures = features.map(feature => ({
      type: 'Feature' as const,
      properties: {
        id: feature.id,
        name: feature.name,
        description: feature.description,
        type: feature.type,
        style: feature.style,
        label: feature.label,
        layerId: feature.layerId,
        tripIds: feature.tripIds,
        ...feature.properties,
      },
      geometry: feature.geometry,
    }))

    return {
      type: 'FeatureCollection',
      properties: {
        layerName: layer?.name,
        layerColor: layer?.color,
        layerIcon: layer?.icon,
      },
      features: geoJsonFeatures,
    }
  }

  async importGeoJSONToLayer(
    geojson: GeoJSON.FeatureCollection,
    layerId: string
  ): Promise<string[]> {
    const createdFeatureIds: string[] = []

    for (const feature of geojson.features) {
      const newFeature: Feature = {
        id: crypto.randomUUID(),
        name: feature.properties?.name ?? 'Imported Feature',
        description: feature.properties?.description,
        type: this.mapGeometryTypeToFeatureType(feature.geometry.type),
        geometry: feature.geometry,
        properties: feature.properties ?? {},
        style: feature.properties?.style ?? {},
        label: feature.properties?.label,
        tags: [],
        layerId: layerId,
        tripIds: feature.properties?.tripIds ?? [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: authService.getCurrentUserId(),
        isVisible: true,
        bounds: this.calculateFeatureBounds(feature.geometry),
      }

      await db.features.add(newFeature)
      await this.addFeatureToLayer(newFeature.id, layerId)
      createdFeatureIds.push(newFeature.id)
    }

    return createdFeatureIds
  }

  // ===== Helper Methods =====
  private getDefaultColor(): string {
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899']
    return colors[Math.floor(Math.random() * colors.length)]
  }

  private mapGeometryTypeToFeatureType(
    geometryType: string
  ): 'point' | 'line' | 'polygon' | 'circle' | 'rectangle' {
    switch (geometryType) {
      case 'Point':
        return 'point'
      case 'LineString':
        return 'line'
      case 'Polygon':
        return 'polygon'
      default:
        return 'point'
    }
  }

  private calculateFeatureBounds(
    geometry: GeoJSON.Geometry
  ): { ne: [number, number]; sw: [number, number] } | undefined {
    const geom = geometry as GeoJSON.Geometry & { coordinates?: unknown }
    if (!geom.coordinates) {
      return undefined
    }

    let minLng = Infinity,
      maxLng = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity

    const processCoordinate = (coord: number[]): void => {
      const [lng, lat] = coord
      minLng = Math.min(minLng, lng)
      maxLng = Math.max(maxLng, lng)
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
    }

    const processCoordinates = (coords: unknown): void => {
      if (Array.isArray(coords) && Array.isArray(coords[0])) {
        coords.forEach((coord: unknown) => processCoordinates(coord))
      } else if (Array.isArray(coords)) {
        processCoordinate(coords as number[])
      }
    }

    if (geometry.type === 'Point') {
      processCoordinate(geometry.coordinates)
    } else {
      processCoordinates(geom.coordinates)
    }

    if (minLng === Infinity) {
      return undefined
    }

    return {
      ne: [maxLat, maxLng],
      sw: [minLat, minLng],
    }
  }

  // ===== Cleanup =====
  async clearAllData(): Promise<void> {
    await db.layerGroups.clear()
    this.layerPanelOpen = false
  }
}

export const layerGroupManager = LayerGroupManager.getInstance()
